import { useCallback, useEffect, useRef, useState } from "react";
import { useLanguage } from "../languageContext";
import translations from "../translation.json";
import { ApiUrl } from "../ApiUrl";
import { toast } from "react-toastify";
import Loader from "../loader";
import ClientsFilterbar from "./clientsFilterbar";
import DraggableHeaderCell from "./EventsPage/DraggableHeaderCell";
import { horizontalListSortingStrategy } from "@dnd-kit/sortable";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { debounce } from "lodash";
import useGetViewsData from "../hooks/useGetViewsData";
import { useGetEventColumns } from "../hooks/useGetEventColumns";
import AddViewModal from "./EventsPage/AddViewModal";
import { AllClientColoumnData } from "../utils/data";
import EventViewHeader from "./EventViewHeader";
import { format } from "date-fns";
import * as XLSX from "xlsx";

const normalizeCallStatus = (status) =>
  status.trim().replace(/\s+/g, " ").toLowerCase();

const CALLBACK_CALL_STATUSES = new Set(
  [
    "A111",
    "Appel filtré",
    "Call screening",
    "A13",
    "ND pendant le VIP",
    "Unavailable",
    "A15",
    "RA-Rappels",
    "Callback",
    "A14",
    "SB- Va P-Ê y allerva rappeler",
    "Might attend-Will callback",
    "B6",
    "RE13-Message au conjoint",
    "Message to spouse",
    "B8",
    "RE17-Demeure loin concession",
    "Lives far from dealer",
  ].map(normalizeCallStatus),
);

const filterCallbackClients = (clients) =>
  (clients || []).filter(
    (client) =>
      typeof client?.call_status === "string" &&
      CALLBACK_CALL_STATUSES.has(normalizeCallStatus(client.call_status)),
  );

const CallbackList = ({
  isDarkMode,
  setDisplayComponent,
  seListId,
  setIsLoading,
}) => {
  const { language } = useLanguage();
  const {
    noShowOptions,
    visitStatusOptions,
    confirmationStatusOptions,
    prospectiveSourceOptions,
    prospectiveSourceOptions2,
    appointmentOption,
    textOptions2,
    textOptions,
    allfields,
    defaultViewColumns,
  } = useGetEventColumns(language);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );
  const clientsFilterData = JSON.parse(
    localStorage.getItem("clientsFilterData"),
  );
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  // Existing portal navigation uses history.pushState rather than router navigation.
  const searchParams = new URLSearchParams(window.location.search);
  const eventId = searchParams.get("event_id") || searchParams.get("eventId");
  const [checkedFields, setCheckedFields] = useState(clientsFilterData || []);
  const token = localStorage.getItem("token");
  const [filtersData, setFiltersData] = useState(
    () => JSON.parse(localStorage.getItem("clientsFilterPostData")) || [],
  );
  const [dealershipDetail, setDealershipDetail] = useState("");
  const [callStatusOptions, setCallStatusOptions] = useState([]);
  const userData = JSON.parse(localStorage.getItem("userData"));
  const userType = localStorage.getItem("userType");
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [prospectiveType, setProspectiveType] = useState([]);
  const viewData = useGetViewsData("client");
  const [tableViews, setTableViews] = useState([]);
  const [selectedViewData, setSelectedViewData] = useState(null);
  const [currentView, setCurrentView] = useState(defaultViewColumns);
  const columnWidthsRef = useRef({});
  const pendingStateUpdate = useRef(false);

  function updateTooltipPositioning() {
    const tooltips = document.querySelectorAll(".custom-tooltip");
    const windowWidth = window.innerWidth;

    tooltips.forEach((tooltip) => {
      const rect = tooltip.getBoundingClientRect();
      const tooltipText = tooltip.querySelector(".custom-tooltiptext");

      // Remove existing positioning classes
      tooltip.classList.remove("left-edge", "right-edge");

      // Check left edge (less than 100px from left)
      if (rect.left < 100) {
        tooltip.classList.add("left-edge");
      }
      // Check right edge (less than 100px from right)
      else if (windowWidth - rect.right < 100) {
        tooltip.classList.add("right-edge");
      }

      // Force recalculation of tooltip width to ensure content fits
      // This is especially important for left-aligned tooltips
      if (tooltip.classList.contains("left-edge")) {
        const maxWidth = Math.min(300, windowWidth - 20); // Ensure it's not wider than viewport
        tooltipText.style.maxWidth = `${maxWidth}px`;
      }
    });
  }

  const processColumns = useCallback(
    (columns) => {
      return columns.map((col) => {
        const newCol = { ...col };

        // CSS sizes are already resolved; only evaluate language expressions.
        if (
          typeof newCol.width === "string" &&
          newCol.width.includes("language")
        ) {
          try {
            newCol.width = Function(
              "language",
              `return (${newCol.width});`,
            )(language);
          } catch (e) {
            console.error("Error evaluating width:", col.width, e);
          }
        }

        if (
          typeof newCol.minWidth === "string" &&
          newCol.minWidth.includes("language")
        ) {
          try {
            newCol.minWidth = Function(
              "language",
              `return (${newCol.minWidth});`,
            )(language);
          } catch (e) {
            console.error("Error evaluating minWidth:", col.minWidth, e);
          }
        }

        // Evaluate content (only if accessing translations)
        if (
          typeof newCol.content === "string" &&
          newCol.content.includes("translations")
        ) {
          try {
            newCol.content = Function(
              "language",
              "translations",
              `return ${newCol.content};`,
            )(language, translations);
          } catch (e) {
            console.error("Error evaluating content:", col.content, e);
          }
        }

        return newCol;
      });
    },
    [language],
  );

  useEffect(() => {
    if (!token || !eventId) {
      setLoading(false);
      setIsLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    // Read the saved filters when loading the event, not as effect dependencies.
    const savedFilterData = JSON.parse(
      localStorage.getItem("clientsFilterData"),
    );
    const savedFilterPostData = JSON.parse(
      localStorage.getItem("clientsFilterPostData"),
    );

    const fetchData = async () => {
      try {
        const res = await fetch(ApiUrl + `/api/user/prospective-index/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(
            savedFilterData?.length > 0
              ? {
                  ...savedFilterPostData,
                  private_event_id: eventId,
                  all_prospects: false,
                }
              : {
                  private_event_id: eventId,
                  all_prospects: false,
                },
          ),
        });
        const clientsData = await res.json();
        if (!active) return;
        if (clientsData.status === "success") {
          const callbackClients = filterCallbackClients(
            clientsData.prospectives,
          );
          const callOptions =
            callbackClients.length > 0
              ? Array.from(
                  new Set(
                    callbackClients
                      .filter((item) => item?.call_status != null) // Filter out null or undefined call_status
                      .map((item) => item.call_status.toString()), // Map to call_status strings
                  ),
                ).map((call_status) => ({
                  label: call_status,
                  value: call_status,
                }))
              : [];

          setClients(callbackClients);
          setCallStatusOptions(callOptions);
          seListId({
            list_id: clientsData.list_id,
            list_name: clientsData.list_name,
          });
          setDealershipDetail(clientsData.dealershipDetails);
        } else {
          toast.error(clientsData.message);
        }
      } catch (err) {
        if (active) toast.error(translations.alertMessages.wentWrong[language]);
      }
    };

    const fetchProspectTypeOptions = async () => {
      try {
        const res = await fetch(ApiUrl + "/api/user/external-user/" + eventId, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const prospectTypesData = await res.json();
        if (!active) return;
        if (prospectTypesData.status === "success") {
          const typeOptions = prospectTypesData?.prospective_type?.length
            ? prospectTypesData.prospective_type.map((list) => ({
                label: list,
                value: list,
              }))
            : [];

          setProspectiveType(typeOptions);
        } else {
          toast.error(prospectTypesData.message);
        }
      } catch (err) {
        if (active) toast.error(translations.alertMessages.wentWrong[language]);
      }
    };

    Promise.all([fetchData(), fetchProspectTypeOptions()]).finally(() => {
      if (active) {
        setLoading(false);
        setIsLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [eventId, token, language, seListId, setIsLoading]);

  const handleCheckboxChange = (clientId) => {
    setSelectedClients(
      (prevSelected) =>
        prevSelected.includes(clientId)
          ? prevSelected.filter((id) => id !== clientId) // Deselect if selected
          : [...prevSelected, clientId], // Add if not selected
    );
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      // Deselect all
      setSelectedClients([]);
    } else {
      // Select all appointments' IDs
      const allClientIds = clients.map((client) => client.id);
      setSelectedClients(allClientIds);
    }
    setSelectAll(!selectAll); // Toggle the state of selectAll
  };

  const getFilteredEvents = async (filterPost, checkedFieldsData) => {
    // setIsLoading(true);
    // setAllowScrollFetch(false);

    try {
      const res = await fetch(ApiUrl + "/api/user/prospective-index", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          private_event_id: eventId,
          ...filterPost,
          all_prospects: false,
        }),
      });
      const clientsData = await res.json();
      if (clientsData.status === "success") {
        toast.success(clientsData.message);
        localStorage.setItem(
          "clientsFilterPostData",
          JSON.stringify(filterPost),
        );
        localStorage.setItem(
          "clientsFilterData",
          JSON.stringify(checkedFieldsData),
        );
        setClients(filterCallbackClients(clientsData.prospectives));
      } else {
        toast.error(clientsData.message);
      }
    } catch (err) {
      // setIsLoading(false);
      toast.error(translations.alertMessages.wentWrong[language]);
    } finally {
      // setAllowScrollFetch(true);
      setLoading(false);
    }
  };

  const getTranslatedConfirmationStatus = (status) => {
    const option = confirmationStatusOptions.find(
      (opt) => opt.value === status,
    );
    return option ? option.label : status;
  };
  const getTranslatedVisitStatus = (status) => {
    const option = visitStatusOptions.find((opt) => opt.value === status);
    return option ? option.label : status;
  };

  const getTranslatedNoShowStatus = (status) => {
    const option = noShowOptions.find((opt) => opt.value === status);
    return option ? option.label : status;
  };

  const exportData = (fileType) => {
    if (!currentView || !currentView.columns) {
      console.error("Current view is not set or missing columns.");
      return;
    }

    const getTranslatedColumnHeader = (columnId) => {
      const columnTranslationMap = {
        id: "ID",
        prospect_type: translations.clients.table.prospectiveType[language],
        prospect_source:
          translations.createProspective.prospectiveSource[language],
        first_name: translations.commonFields.firstName[language],
        last_name: translations.commonFields.lastName[language],
        phone: translations.quickCreationModal.phoneNumber[language],
        office_phone: translations.createProspective.officeNumber[language],
        email: translations.commonFields.email[language],
        og_rep: translations.commonWords.usersTypes.representative[language],
        call_status: translations.clients.table.callStatus[language],
        make: translations.createProspective.make[language],
        model: translations.createProspective.model[language],
        year: translations.usersPage.createUser.year[language],
        general_comment:
          translations.createProspective.generalComment[language],
        km: "KM",
        address: translations.commonFields.address[language],
        city: translations.commonFields.city[language],
        province: translations.commonFields.province[language],
        postal_code:
          translations.forms.formFields.conquestSection.postalCode[language],
        appointment: translations.appointments.appointment[language],
        mobile_phone: translations.commonFields.mobileNumber[language],
        language: translations.commonFields.language[language],
        vin: translations.createProspective.VIN[language],
        purchase_date: translations.createProspective.purchaseDate[language],
        last_service_date:
          translations.createProspective.lastServiceDate[language],
        greetings: translations.createProspective.greetings[language],
        confirmation_status:
          translations.createProspective.confirmationStatus[language],
        confirmation_notes:
          translations.createProspective.confirmationNotes[language],
        visit_status: translations.createProspective.visitStatus[language],
        visit_notes: translations.createProspective.visitNotes[language],
        no_show_status: translations.clients.table.noShowStatus[language],
        no_show_notes: translations.createProspective.noShowNotes[language],
        appointment_rep_user_name:
          translations.createProspective.appointmentRepresentative[language],
        appointment_time: translations.clients.table.appointmentTime[language],
        appointment_date: translations.clients.table.appointmentDate[language],
        appointment_comment:
          translations.createProspective.appointmentComment[language],
        import_note: translations.createProspective.import_note[language],
        group_code: translations.createProspective.group_code[language],
        create_by: translations.createProspective.bookedBy[language],
        actions: translations.EventsPortal.eventsTableHeader.actions[language],
      };

      return columnTranslationMap[columnId] || columnId;
    };

    const selectedColumns = currentView.columns
      .slice(0, -1)
      .filter((item) => item.renderInView !== false)
      .map((col) => ({
        key: col.id,
        header: getTranslatedColumnHeader(col.id),
      }));

    const selectedData =
      clients && clients.length > 0
        ? clients
            .filter((client) => selectedClients.includes(client.id))
            .map((client) => {
              const rowData = {};

              selectedColumns.forEach(({ key, header }) => {
                let value = "";

                if (key === "prospect_source") {
                  if (client?.prospect_source) {
                    const matchedOption = prospectiveSourceOptions.find(
                      (option) =>
                        option.value.en?.toLowerCase() ===
                          client?.prospect_source?.toLowerCase() ||
                        option.value.fr?.toLowerCase() ===
                          client?.prospect_source?.toLowerCase(),
                    );
                    value = matchedOption
                      ? matchedOption.label[language]
                      : client?.prospect_source;
                  }
                } else if (key === "confirmation_status") {
                  value = getTranslatedConfirmationStatus(
                    client?.appointments?.[0],
                  )?.confirmation_status;
                } else if (key === "confirmation_notes") {
                  value = client?.appointments?.[0]?.confirmation_notes || "";
                } else if (key === "visit_status") {
                  value = getTranslatedVisitStatus(
                    client?.appointments?.[0]?.visit_status,
                  );
                } else if (key === "visit_notes") {
                  value = client?.appointments?.[0]?.visit_notes || "";
                } else if (key === "no_show_status") {
                  value = getTranslatedNoShowStatus(
                    client?.appointments?.[0]?.no_show_status,
                  );
                } else if (key === "no_show_notes") {
                  value = client?.appointments?.[0]?.no_show_notes || "";
                } else if (key === "appointment") {
                  value =
                    client[key] === 0 || client[key] === "0" ? "No" : "Yes";
                } else if (key === "create_by") {
                  value = client[key]?.name || "";
                } else {
                  value =
                    client?.appointments?.[0]?.[key] || client[key] || "";
                }

                rowData[header] = value;
              });

              return rowData;
            })
        : [];

    const worksheet = XLSX.utils.json_to_sheet(selectedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Exported Data");

    const exportBaseName =
      language === "fr" ? "Exportation_Callbacks" : "Export_Callbacks";

    if (fileType === "csv") {
      const csvData = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csvData], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${exportBaseName}_${format(new Date(), "dd/MM/yyyy")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      XLSX.writeFile(
        workbook,
        `${exportBaseName}_${format(new Date(), "dd/MM/yyyy")}.xlsx`,
      );
    }
  };

  const moveColumn = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = currentView.columns.findIndex(
      (col) => col.id === active.id,
    );
    const newIndex = currentView.columns.findIndex((col) => col.id === over.id);

    const reorderedColumns = arrayMove(currentView.columns, oldIndex, newIndex);
    const updatedData = { ...currentView, columns: reorderedColumns };
    setCurrentView((prevView) => ({ ...prevView, columns: reorderedColumns }));
    if (updatedData.type !== "default client") {
      updateView(updatedData);
    }
  };

  const handleResize = (columnId, newWidth) => {
    columnWidthsRef.current[columnId] = newWidth;
    requestAnimationFrame(() => {
      const columnElement = document.getElementById(columnId);
      if (columnElement) columnElement.style.width = `${newWidth}px`;
    });
    // Mark that a state update is needed
    if (!pendingStateUpdate.current) {
      pendingStateUpdate.current = true;
      debouncedUpdate(setCurrentView);
    }
  };

  // Debounce for smoother updates instead of throttle
  const debouncedUpdate = debounce((setCurrentView) => {
    const updatedData = {
      ...currentView,
      columns: currentView.columns.map((col) =>
        col.id in columnWidthsRef.current
          ? { ...col, width: columnWidthsRef.current[col.id] }
          : col,
      ),
    };
    setCurrentView((prevColumns) => ({
      ...prevColumns,
      columns: prevColumns.columns.map((col) =>
        col.id in columnWidthsRef.current
          ? { ...col, width: columnWidthsRef.current[col.id] }
          : col,
      ),
    }));
    if (updatedData.type !== "default client") {
      updateView(updatedData);
    }
    pendingStateUpdate.current = false; // Reset flag after update
  }, 16); // Adjust delay as needed

  const updateView = async (updatedData) => {
    try {
      const res = await fetch(ApiUrl + "/api/user/views", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });
      const updatedViewData = await res.json();
      if (updatedViewData.status === "success") {
        if (updatedViewData.userView) {
          const savedView = {
            ...updatedViewData.userView,
            columns: processColumns(
              typeof updatedViewData.userView.columns === "string"
                ? JSON.parse(updatedViewData.userView.columns)
                : updatedViewData.userView.columns || [],
            ),
          };
          setTableViews((prev) =>
            prev.map((view) => (view.id === updatedData.id ? savedView : view)),
          );
        }
      } else {
        toast.error(translations.alertMessages.wentWrong[language]);
      }
    } catch (error) {
      toast.error(translations.alertMessages.wentWrong[language]);
    }
  };

  useEffect(() => {
    // Initial call
    updateTooltipPositioning();

    // Add resize listener
    window.addEventListener("resize", updateTooltipPositioning);

    const targetNode = document.querySelector(".headersec");
    const observer = new MutationObserver(updateTooltipPositioning);

    if (targetNode) {
      observer.observe(targetNode, {
        childList: true,
        subtree: true,
      });
    } else {
      console.warn(
        "Element '.headersec' not found, skipping MutationObserver setup.",
      );
    }

    return () => {
      window.removeEventListener("resize", updateTooltipPositioning);
      observer.disconnect();
    };
  }, [loading]);

  useEffect(() => {
    if (viewData?.user_views) {
      const processedViews = viewData?.user_views?.map((view) => ({
        ...view,
        columns: processColumns(view.columns || []),
      }));
      setTableViews(processedViews);

      const currentViewObj = processedViews.find(
        (item) =>
          item.current === "1" || item.current === 1 || item.current === true,
      );

      if (currentViewObj) {
        setCurrentView({
          ...currentViewObj,
          columns: currentViewObj.columns || [],
        });
      }
    }
  }, [viewData, processColumns]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => debouncedUpdate.cancel();
  }, [viewData]);

  return (
    <div>
      {!loading ? (
        <div className="container maincont">
          <div
            id="app"
            className={`appcountainer2 client-table-container${
              clients?.length ? "" : " d-flex flex-column"
            }`}
          >
            <AddViewModal
              isDarkMode={isDarkMode}
              getColumns={AllClientColoumnData}
              setTableViews={setTableViews}
              setIsLoading={setIsLoading}
              viewData={selectedViewData}
              setViewData={setSelectedViewData}
              setCurrentView={setCurrentView}
              tableType="client"
            />
            <ClientsFilterbar
              isDarkMode={isDarkMode}
              allfields={allfields}
              checkedFields={checkedFields}
              setCheckedFields={setCheckedFields}
              textOptions={textOptions}
              textOptions2={textOptions2}
              appointmentOption={appointmentOption}
              prospectiveSourceOptions={prospectiveSourceOptions2}
              callStatusOptions={callStatusOptions}
              filtersData={filtersData}
              setFiltersData={setFiltersData}
              getFilteredEvents={getFilteredEvents}
              prospectiveType={prospectiveType}
            />
            <EventViewHeader
              eventId={eventId}
              isDarkMode={isDarkMode}
              setDisplayComponent={setDisplayComponent}
              activePage="callback-list"
              title={translations.callbacks.title[language]}
              dealershipDetails={dealershipDetail}
            >
              {userType &&
                selectedClients.length > 0 &&
                userData?.roles?.[0]?.permissions?.some(
                  (item) => item.name === "Event Data Export",
                ) && (
                  <div
                    className="btn-group"
                    role="group"
                    aria-label="Basic example"
                  >
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className="btn admin-btn btn-primary edit-button d-flex align-items-center justify-content-center gap-1 mx-auto"
                        style={{
                          width: "100%",
                          maxWidth: "150px",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                        onClick={() => exportData("xlsx")}
                      >
                        <i className="bi bi-filetype-xlsx"></i>{" "}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger edit-button d-flex align-items-center justify-content-center gap-1 mx-auto"
                        style={{
                          width: "100%",
                          maxWidth: "150px",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                        onClick={() => exportData("csv")}
                      >
                        <i className="bi bi-filetype-csv"></i>{" "}
                      </button>
                    </div>
                  </div>
                )}
              <div
                className="btn-group"
                style={{ width: "250px", maxWidth: "calc(100% - 50px)" }}
              >
                <button
                  type="button"
                  className="btn btn-light dropdown-toggle text-truncate"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {currentView?.name === "Default View"
                    ? translations.EventsPortal.views.default[language]
                    : currentView?.name}
                </button>
                <ul
                  className="dropdown-menu dropdown-menu-end"
                  style={{
                    minWidth: "250px",
                    background: isDarkMode ? "#2b3035" : "#FFFFFF",
                  }}
                >
                  <li style={{ maxHeight: "200px", overflowY: "auto" }}>
                    <ul className="list-unstyled mb-0">
                      {(tableViews.length ? tableViews : [currentView]).map(
                        (view) => (
                          <li
                            key={view.id ?? view.type}
                            className={`d-flex align-items-center px-2 ${
                              isDarkMode
                                ? "views-hover-dark"
                                : "views-hover"
                            }`}
                          >
                            <button
                              type="button"
                              className="dropdown-item flex-grow-1 px-0"
                              onClick={() => {
                                const selectedView = {
                                  ...view,
                                  current: true,
                                };
                                setTableViews((prev) =>
                                  prev.map((item) => ({
                                    ...item,
                                    current: item.id === view.id,
                                  })),
                                );
                                setCurrentView(selectedView);
                                if (view.id != null)
                                  updateView(selectedView);
                              }}
                            >
                              {view.name === "Default View"
                                ? translations.EventsPortal.views.default[
                                    language
                                  ]
                                : language === "fr" &&
                                    view.name === "Contract and Billing"
                                  ? "Contrat et facturation"
                                  : view.name}
                            </button>
                            <i
                              className="bi bi-star-fill mx-2"
                              style={{
                                color:
                                  currentView?.id === view.id
                                    ? "gold"
                                    : "#adb5bd",
                              }}
                              aria-hidden="true"
                            />
                            <button
                              type="button"
                              className="btn btn-sm border-0"
                              disabled={view.type === "default client"}
                              aria-label={`${translations.EventsPortal.eventsTableButtons.edit[language]} ${view.name}`}
                              data-bs-toggle="modal"
                              data-bs-target="#addViewModal"
                              onClick={() =>
                                setSelectedViewData({
                                  ...view,
                                  mode: "edit",
                                })
                              }
                            >
                              <i
                                className="bi bi-pencil-fill"
                                aria-hidden="true"
                              />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm border-0 text-danger"
                              disabled={
                                currentView?.id === view.id ||
                                view.type === "default client"
                              }
                              aria-label={`${translations.EventsPortal.eventsTableButtons.delete[language]} ${view.name}`}
                              data-bs-toggle="modal"
                              data-bs-target="#addViewModal"
                              onClick={() =>
                                setSelectedViewData({
                                  ...view,
                                  mode: "delete",
                                })
                              }
                            >
                              <i
                                className="bi bi-trash-fill"
                                aria-hidden="true"
                              />
                            </button>
                          </li>
                        ),
                      )}
                    </ul>
                  </li>
                  <li className="px-2 mt-2">
                    <button
                      type="button"
                      className="btn btn-light w-100"
                      data-bs-toggle="modal"
                      data-bs-target="#addViewModal"
                      onClick={() => setSelectedViewData(null)}
                    >
                      <i className="bi bi-plus" aria-hidden="true" />{" "}
                      {translations.EventsPortal.views.addNewView[language]}
                    </button>
                  </li>
                </ul>
              </div>
              <div
                className="btn-group flex-shrink-0 text-nowrap"
                role="group"
                aria-label="Basic example"
              >
                <button
                  id=""
                  type="button"
                  className="btn admin-btn btn-primary"
                  data-bs-target="#clientFiltersidebar"
                  data-bs-toggle="offcanvas"
                  aria-controls="clientFiltersidebar"
                  aria-label={
                    translations.EventsPortal.searchbar.search[language]
                  }
                >
                  <i className="bi bi-search"></i>{" "}
                </button>
              </div>
            </EventViewHeader>
            <div className="headersec row">
              <div className="mb-3 col-12">
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  {clientsFilterData &&
                    clientsFilterData?.length > 0 &&
                    clientsFilterData?.map((item, index) => (
                      <div
                        key={index}
                        className="d-flex py-1 px-2 gap-2 align-items-center justify-content-between custom-tooltip"
                        style={{
                          border: isDarkMode
                            ? "1px solid #495057"
                            : "1px solid #dee2e6",
                          borderRadius: "0.275rem",
                          minWidth: "150px",
                          maxWidth: "250px",
                        }}
                      >
                        <span className="custom-tooltiptext">{`${
                          item?.content
                        } : ${
                          Array.isArray(item?.value)
                            ? item.value
                                .map(
                                  (id) =>
                                    (item.name === "appointment"
                                      ? appointmentOption
                                      : item.name === "prospect_source"
                                        ? prospectiveSourceOptions
                                        : item.name === "prospective_type"
                                          ? prospectiveType
                                          : item.name === "call_status"
                                            ? callStatusOptions
                                            : textOptions
                                    )?.find(
                                      (option) =>
                                        option.value?.toString() ===
                                        id?.toString(),
                                    )?.label || id,
                                )
                                ?.join(", ")
                            : item?.value
                        }`}</span>
                        <div
                          className="d-flex align-items-center gap-2 justify-content-start"
                          style={{ width: "90%" }}
                        >
                          {/* Container for Content */}
                          <div className="text-truncate">
                            <span>
                              {item?.content}{" "}
                              {translations.commonWords.is[language]}{" "}
                            </span>
                          </div>
                          {/* Container for Value */}
                          <div
                            className="text-truncate"
                            style={{
                              maxWidth: "60%",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {Array.isArray(item?.value)
                              ? item.value
                                  .map(
                                    (id) =>
                                      (item.name === "appointment"
                                        ? appointmentOption
                                        : item.name === "prospect_source"
                                          ? prospectiveSourceOptions
                                          : item.name === "prospective_type"
                                            ? prospectiveType
                                            : item.name === "call_status"
                                              ? callStatusOptions
                                              : textOptions
                                      )?.find(
                                        (option) =>
                                          option.value?.toString() ===
                                          id?.toString(),
                                      )?.label || id,
                                  )
                                  ?.join(", ")
                              : item?.value}
                          </div>
                        </div>

                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            let updatedCheckedFields = checkedFields.filter(
                              (field) => field.name !== item.name,
                            );
                            setCheckedFields(updatedCheckedFields);

                            const keysToRemove = [item.name];
                            const updatedFiltersData = Object.fromEntries(
                              Object.entries(filtersData).filter(
                                ([key]) => !keysToRemove.includes(key),
                              ),
                            );

                            setFiltersData(updatedFiltersData);
                            getFilteredEvents(
                              updatedFiltersData,
                              updatedCheckedFields,
                            );
                          }}
                        >
                          <i
                            className="bi bi-x-circle-fill"
                            style={{
                              color: isDarkMode ? "#495057" : "#868686",
                            }}
                          ></i>
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            {!loading && clients && clients.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={moveColumn}
              >
                <SortableContext
                  items={
                    currentView &&
                    currentView?.columns?.slice(1, -1)?.map((col) => col.id)
                  }
                  strategy={horizontalListSortingStrategy}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      overflowX: "auto",
                      justifyContent: "space-between",
                      // height: "calc(100vh - 260px)",
                      height: "auto",
                      maxHeight: `calc(100vh - ${clientsFilterData?.length > 0 ? "329px" : "295px"} )`,
                    }}
                  >
                    <table
                      // className="table table-hover text-center align-middle"
                      className="align-content-center align-middle"
                      style={{
                        minWidth: "100%",
                        borderCollapse: "collapse",
                        tableLayout: "fixed",
                      }}
                    >
                      <thead
                        style={{ position: "sticky", top: "0", zIndex: "1" }}
                      >
                        <tr
                          style={{
                            background: isDarkMode ? "#1b1f22" : "#ffffff",
                            borderBottom: "1px solid",
                          }}
                        >
                          {userData &&
                            (userData.roles[0].name === "Super Admin" ||
                              userData.roles[0].name === "Admin") && (
                              <th
                                scope="col"
                                className={`${isDarkMode ? "darkthead" : ""}`}
                                style={{ width: "20px" }}
                              >
                                {
                                  <input
                                    type="checkbox"
                                    className=""
                                    checked={selectAll}
                                    onChange={handleSelectAllChange}
                                  />
                                }
                              </th>
                            )}
                          {
                            <th
                              scope="col"
                              className={`${isDarkMode ? "darkthead" : ""} ${
                                currentView?.columns[0]?.className || ""
                              }`}
                              style={{
                                ...currentView?.columns[0]?.style,
                                width: `${currentView?.columns[0]?.width}px`,
                                paddingInline: "56px !important",
                                marginInline: "4px !important",
                                background: isDarkMode ? "#1b1f22" : "#ffffff",
                                // background: isDarkMode ? "#343a3f" : "#ffffff",
                              }}
                              colSpan={currentView?.columns[0]?.colSpan}
                            >
                              {currentView?.columns[0]?.content}
                            </th>
                          }
                          {currentView?.columns
                            .slice(1, -1)
                            .filter((header) => header.renderInView !== false)
                            .map((header) =>
                              header.showIf !== false ? (
                                <DraggableHeaderCell
                                  key={header.id}
                                  column={header}
                                  isDarkMode={isDarkMode}
                                  onResize={handleResize}
                                />
                              ) : null,
                            )}

                          {currentView?.columns
                            .filter((header) => header.renderInView !== false)
                            .some((column) => column.id === "actions") && (
                            <th
                              scope="col"
                              className={`${isDarkMode ? "darkthead" : ""} ${
                                currentView?.columns[
                                  currentView?.columns.length - 1
                                ]?.className || ""
                              }`}
                              style={{
                                ...currentView?.columns[
                                  currentView.columns.length - 1
                                ]?.style,
                                width: `${
                                  currentView?.columns[
                                    currentView?.columns.length - 1
                                  ]?.width
                                }px`,
                                paddingInline: "56px !important",
                                marginInline: "4px !important",
                                background: isDarkMode ? "#1b1f22" : "#ffffff",
                                // background: isDarkMode ? "#343a3f" : "#ffffff",
                                position: "sticky",
                                right: "0px",
                              }}
                              colSpan={
                                currentView?.columns[
                                  currentView?.columns.length - 1
                                ]?.colSpan
                              }
                            >
                              {
                                currentView?.columns[
                                  currentView?.columns.length - 1
                                ]?.content
                              }
                            </th>
                          )}
                        </tr>
                      </thead>
                      {clients.map((client, index) => {
                        return (
                          <>
                            <tr
                              className="result-box highlight-text-onhover"
                              key={client?.id}
                              style={{ borderBottom: "1px solid" }}
                            >
                              {userData &&
                                (userData.roles[0].name === "Super Admin" ||
                                  userData.roles[0].name === "Admin") && (
                                  <td className="fw-bold">
                                    {
                                      <input
                                        type="checkbox"
                                        className=""
                                        checked={selectedClients.includes(
                                          client.id,
                                        )}
                                        onChange={() =>
                                          handleCheckboxChange(client.id)
                                        }
                                      />
                                    }
                                  </td>
                                )}
                              <th
                                className="custID highlight-text-color"
                                scope="row"
                                style={{ paddingInline: "25px" }}
                              >
                                {client?.id}
                              </th>
                              {currentView &&
                                currentView?.columns &&
                                currentView?.columns
                                  .slice(1, -1)
                                  .filter(
                                    (header) => header.renderInView !== false,
                                  )
                                  .map((col) => {
                                    if (col.showIf === false) return null; // Skip columns with showIf: false

                                    const columnKey = col.id;

                                    return (
                                      <td
                                        className="highlight-text-color "
                                        scope="row"
                                        id={columnKey}
                                        key={col.id}
                                        style={col.style}
                                      >
                                        {columnKey === "prospect_source"
                                          ? client?.prospect_source
                                            ? (() => {
                                                const matchedOption =
                                                  prospectiveSourceOptions.find(
                                                    (option) =>
                                                      option.value.en?.toLowerCase() ===
                                                        client?.prospect_source?.toLowerCase() ||
                                                      option.value.fr?.toLowerCase() ===
                                                        client?.prospect_source?.toLowerCase(),
                                                  );

                                                // Return the label in the current language if a match is found
                                                return matchedOption
                                                  ? matchedOption.label[
                                                      language
                                                    ]
                                                  : client?.prospect_source;
                                              })()
                                            : ""
                                          : columnKey === "confirmation_status"
                                            ? getTranslatedConfirmationStatus(
                                                client?.appointments[0],
                                              )?.confirmation_status
                                            : columnKey === "confirmation_notes"
                                              ? client?.appointments[0]
                                                  ?.confirmation_notes
                                              : columnKey === "visit_status"
                                                ? getTranslatedVisitStatus(
                                                    client?.appointments[0]
                                                      ?.visit_status,
                                                  )
                                                : columnKey === "visit_notes"
                                                  ? client?.appointments[0]
                                                      ?.visit_notes
                                                  : columnKey ===
                                                      "no_show_status"
                                                    ? getTranslatedNoShowStatus(
                                                        client?.appointments[0]
                                                          ?.no_show_status,
                                                      )
                                                    : columnKey ===
                                                        "no_show_notes"
                                                      ? client?.appointments[0]
                                                          ?.no_show_notes
                                                      : columnKey ===
                                                          "appointment"
                                                        ? client[columnKey] ===
                                                            0 ||
                                                          client[columnKey] ===
                                                            "0"
                                                          ? "No"
                                                          : "Yes"
                                                        : columnKey ===
                                                            "create_by"
                                                          ? client[columnKey]
                                                              ?.name
                                                          : client
                                                              ?.appointments[0]?.[
                                                              columnKey
                                                            ] ||
                                                            client[columnKey] ||
                                                            ""}
                                      </td>
                                    );
                                  })}

                              {currentView?.columns
                                .filter(
                                  (header) => header.renderInView !== false,
                                )
                                .some((column) => column.id === "actions") && (
                                <>
                                  <td
                                    style={{
                                      background: isDarkMode
                                        ? "#212529"
                                        : "#ffffff",
                                      position: "-webkit-sticky",
                                      position: "sticky",
                                      right: 0,
                                    }}
                                    className="highlight-text-action"
                                  >
                                    <div className="d-flex justify-content-end align-items-center gap-2">
                                      <div>
                                        <button
                                          type="button"
                                          className="btn btn-light edit-button d-flex align-items-center justify-content-center gap-1 mx-auto"
                                          onClick={() => {
                                            setDisplayComponent(
                                              "edit-prospective",
                                            );
                                            //   setEditEventID(event.id);
                                            window.history.pushState(
                                              {
                                                id: "gallery",
                                                randomData:
                                                  window.Math.random(),
                                              },
                                              "title",
                                              "/event-portal?page=edit-prospective&event_id=" +
                                                eventId +
                                                `&client_id=${client?.id}`,
                                            );
                                          }}
                                        >
                                          <i className="bi bi-eye"></i>{" "}
                                          {
                                            translations.commonWords.view[
                                              language
                                            ]
                                          }
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          </>
                        );
                      })}
                    </table>
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div
                className="d-flex flex-column flex-grow-1 align-items-center justify-content-center text-center px-4 py-5"
                role="status"
                style={{ minHeight: "240px", color: "var(--color-text)" }}
              >
                <i
                  className="bi bi-telephone fs-1 mb-3"
                  style={{ color: isDarkMode ? "#2ad3b4" : "#087f6b" }}
                  aria-hidden="true"
                />
                <h4 className="mb-2">
                  {translations.callbacks.emptyState.title[language]}
                </h4>
                <p
                  className="mb-0"
                  style={{ maxWidth: "420px", opacity: 0.75 }}
                >
                  {
                    translations.callbacks.emptyState[
                      clientsFilterData?.length
                        ? "filteredDescription"
                        : "description"
                    ][language]
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  );
};

export default CallbackList;
